import type { LhcFilter, LhcItemsFilter } from '@diamond/shared/types';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { type Query, useQuery } from '@tanstack/react-query';

import {
  type CollectionBiltoDetailParams,
  getCollectionBiltoDetail,
  getCollectionPlants,
  getLHCDetail,
  getLHCList,
} from '../api';
import { COLLECTION_QUERY_KEYS } from '../api/constants';

export function useLHCList(params: LhcFilter) {
  const session = useAuthStore();

  return useQuery({
    queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST, params],
    queryFn: () => getLHCList(session.access_token, params),
    enabled: !!session.access_token,
  });
}

export function useCollectionPlants() {
  const session = useAuthStore();

  return useQuery({
    queryKey: [COLLECTION_QUERY_KEYS.COLLECTION_PLANTS],
    queryFn: () => getCollectionPlants(session.access_token),
    enabled: !!session.access_token,
  });
}

export function useLHCDetail(id: string, params?: LhcItemsFilter) {
  const session = useAuthStore();

  return useQuery({
    queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL, id, params],
    queryFn: () => getLHCDetail(session.access_token, id, params),
    enabled: !!session.access_token && !!id,
  });
}

export function useCollectionBiltoDetail(
  sapNoBilto: string,
  params?: CollectionBiltoDetailParams
) {
  const session = useAuthStore();

  return useQuery({
    queryKey: [
      COLLECTION_QUERY_KEYS.COLLECTION_BILTO_DETAIL,
      sapNoBilto,
      params,
    ],
    queryFn: () =>
      getCollectionBiltoDetail(session.access_token, sapNoBilto, params),
    enabled: !!session.access_token && !!sapNoBilto,
    placeholderData: (previousData, previousQuery: Query | undefined) => {
      const prevSap = previousQuery?.queryKey?.[1];
      if (typeof prevSap !== 'string' || prevSap !== sapNoBilto) {
        return undefined;
      }
      return previousData;
    },
  });
}
